import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import ModalWindow from '../../Modals/ModalWindow/ModalWindow';
import ModalButton from '../../Modals/ModalWindow/ModalButton/ModalButton';
import PremiumRewardModal from '../../Modals/PremiumRewardModal/PremiumRewardModal';
import FactionPurchaseRewardModal from '../../Modals/FactionPurchaseRewardModal/FactionPurchaseRewardModal';
import {
  selectIsMissionFailed,
  selectIsMissionFinished,
  selectMissionId,
} from '../../../redux/reducers/privateStateReducer/selectors';
import {
  setAvailableFactions,
  setCompletedCampaignMissions,
  setPremiumExpiresAt,
  updatePlayerRewards,
} from '../../../redux/actions';
import {getCampaignMission} from '../../../configs/campaigns';
import {API_BASE} from '../../../utils/apiConfig';
import {REGISTRY_SINGLETON} from '../../../scenes/Registry';
import {getChestIconPath} from '../../../utils/inventory/getChestIconPath';
import {getChestDisplayName} from '../../../utils/inventory/getChestDisplayName';
import {getFactionConfig} from '../../../utils/factions/getFactionConfig';
import {getFactionIconPath} from '../../../utils/factions/getFactionIconPath';
import {ALL_CHEST_CONFIGS} from '../../../configs/chests';
import '../../Tutorial/TutorialChapterCompleteModal/TutorialChapterCompleteModal.css';

// Пауза между поздравлением и наградной модалкой поверх него — как у глав
// обучения: игрок должен успеть увидеть, что миссия пройдена
const REWARD_MODAL_DELAY_MS = 800;

/**
 * Завершение миссии сюжетной кампании. Полностью повторяет флоу завершения
 * главы обучения (TutorialChapterCompleteModal): на монтировании сохраняет
 * прохождение на сервере, при ПЕРВОМ прохождении получает награду и
 * показывает её поверх поздравления, локально обновляет стейт.
 *
 * При провале миссии награды нет — только предложение переиграть.
 */
const MissionCompleteModal = ({onRestart, onExitToMenu, onOpenInventory}) => {
  const dispatch = useDispatch();
  const missionId = useSelector(selectMissionId);
  const isFinished = useSelector(selectIsMissionFinished);
  const isFailed = useSelector(selectIsMissionFailed);
  const availableFactions = useSelector((state) => state.private.auth.availableFactions);

  const saveRequestedRef = useRef(false);
  const soundPlayedRef = useRef(false);
  // null — ответ сервера ещё не пришёл; {reward: null} — награды нет
  const [saveResult, setSaveResult] = useState(null);
  const [isRewardModalShown, setIsRewardModalShown] = useState(false);
  const [isRewardModalClosed, setIsRewardModalClosed] = useState(false);

  const mission = getCampaignMission(missionId);

  useEffect(() => {
    if (!isFinished || isFailed || saveRequestedRef.current || !missionId) {
      return;
    }
    saveRequestedRef.current = true;

    fetch(`${API_BASE}/users/me/campaign-missions/complete`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      credentials: 'include',
      body: JSON.stringify({missionId}),
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then((data) => {
        if (Array.isArray(data.completedCampaignMissions)) {
          dispatch(setCompletedCampaignMissions(data.completedCampaignMissions));
        }

        // Локальное применение награды — без перезагрузки страницы
        const reward = data.reward || null;
        if (reward?.type === 'premium' && reward.premiumExpiresAt) {
          dispatch(setPremiumExpiresAt(reward.premiumExpiresAt));
        } else if (reward?.type === 'chest') {
          dispatch(updatePlayerRewards(0, undefined, true));
        } else if (reward?.type === 'faction') {
          dispatch(updatePlayerRewards(0, undefined, true));
          const factionConfig = getFactionConfig(reward.factionId);
          const alreadyListed = (availableFactions || []).some(
            (faction) => faction.id === reward.factionId && faction.isAvailable
          );
          if (!alreadyListed) {
            const withoutReward = (availableFactions || []).filter((faction) => faction.id !== reward.factionId);
            dispatch(setAvailableFactions([
              ...withoutReward,
              {id: reward.factionId, name: factionConfig?.name || reward.factionId, isAvailable: true},
            ]));
          }
        }
        setSaveResult({reward});
      })
      .catch((error) => {
        // Прогресс досохранится при следующем прохождении — сервер идемпотентен
        console.error('[Mission] Failed to save mission completion:', error);
        setSaveResult({reward: null});
      });
  }, [isFinished, isFailed, missionId, dispatch, availableFactions]);

  useEffect(() => {
    if (!isFinished || soundPlayedRef.current) return;
    soundPlayedRef.current = true;
    REGISTRY_SINGLETON.getSoundManager().playGameStart();
  }, [isFinished]);

  const reward = saveResult?.reward || null;
  const hasRewardModal = reward?.type === 'premium' || reward?.type === 'faction';
  useEffect(() => {
    if (!hasRewardModal || isRewardModalShown || isRewardModalClosed) {
      return undefined;
    }
    const timeoutId = setTimeout(() => setIsRewardModalShown(true), REWARD_MODAL_DELAY_MS);
    return () => clearTimeout(timeoutId);
  }, [hasRewardModal, isRewardModalShown, isRewardModalClosed]);

  if (!isFinished || !mission) {
    return null;
  }
  // Победа ждёт ответа сервера: от него зависит, показывать ли награду
  if (!isFailed && !saveResult) {
    return null;
  }

  const chestConfig = reward?.type === 'chest'
    ? ALL_CHEST_CONFIGS.find((config) => config.id === reward.chestId)
    : null;
  const factionConfig = reward?.type === 'faction' ? getFactionConfig(reward.factionId) : null;

  let rewardModal = null;
  if (reward && isRewardModalShown && !isRewardModalClosed) {
    if (reward.type === 'premium') {
      rewardModal = <PremiumRewardModal days={reward.days} onClaim={() => setIsRewardModalClosed(true)}/>;
    } else if (reward.type === 'faction') {
      rewardModal = (
        <FactionPurchaseRewardModal
          factionId={reward.factionId}
          onClaim={() => setIsRewardModalClosed(true)}
        />
      );
    }
  }

  return (
    <>
      <ModalWindow className={'tutorial-chapter-complete-modal'} showQuote>
        <h2 className={'tutorial-chapter-complete-modal_title'}>
          {isFailed ? 'Миссия провалена' : 'Миссия пройдена!'}
        </h2>
        <p className={'tutorial-chapter-complete-modal_text'}>
          {isFailed ? 'Условие поражения выполнено. Попробуй ещё раз.' : mission.title}
        </p>

        {!isFailed && reward?.type === 'premium' && (
          <div className={'tutorial-chapter-complete-modal_reward-line'}>
            <img
              className={'tutorial-chapter-complete-modal_reward-icon'}
              src={'/img/icons/premium.png'}
              alt={'Премиум'}
            />
            <span>Награда: <b>Премиум-аккаунт на {reward.days} дня</b></span>
          </div>
        )}

        {!isFailed && reward?.type === 'chest' && (
          <div className={'tutorial-chapter-complete-modal_chest'}>
            <img
              className={'tutorial-chapter-complete-modal_chest-image'}
              src={getChestIconPath(reward.chestId)}
              alt={getChestDisplayName(reward.chestId)}
            />
            <span
              className={'tutorial-chapter-complete-modal_chest-name'}
              style={chestConfig?.textColor ? {color: chestConfig.textColor} : undefined}
            >
              {/* Имя берём из getChestDisplayName, а не из chest.name:
                  у наградных сундуков (chest_silver и прочие ступени лиг)
                  собственного имени в конфиге нет, и в модалке оставалось
                  безымянное «Сундук» */}
              {getChestDisplayName(reward.chestId)}
            </span>
            <span className={'tutorial-chapter-complete-modal_reward-note'}>
              Награда уже в твоей коллекции!
            </span>
          </div>
        )}

        {!isFailed && reward?.type === 'faction' && (
          <div className={'tutorial-chapter-complete-modal_reward-line'}>
            <img
              className={'tutorial-chapter-complete-modal_reward-icon'}
              src={getFactionIconPath(reward.factionId)}
              alt={factionConfig?.name || reward.factionId}
            />
            <span>Награда: фракция <b>{factionConfig?.name || reward.factionId}</b> открыта</span>
          </div>
        )}

        <div className={'tutorial-chapter-complete-modal_buttons'}>
          {isFailed && (
            <ModalButton
              onClick={() => onRestart(mission.id)}
              text={'Пройти заново'}
              className={'tutorial-chapter-complete-modal_next-button'}
            />
          )}
          {!isFailed && reward && (
            <ModalButton onClick={onOpenInventory} text={'Открыть в коллекции'}/>
          )}
          <ModalButton onClick={onExitToMenu} text={'В главное меню'}/>
        </div>
      </ModalWindow>
      {rewardModal}
    </>
  );
};

export default MissionCompleteModal;
